import { useCallback } from 'react';
import {
  Formik,
  Field,
} from 'formik';
import {
  useNavigate,
} from 'react-router';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  toast,
} from 'react-toastify';
import * as Yup from 'yup';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';
import {
  Flex,
  Text,
  SVG,
} from 'web-styled';

import {
  data,
  timezonesOptions,
} from 'web-utils';

import {
  Button,
  TextField,
  RadioField,
  UploadPhoto,
  SelectField,
  ClientNotes,
  PasswordField,
  DatePickerField,
} from 'web-components';

import * as S from './styled';

type Props = {
  clientInfo: any,
  profileId: Number,
};

const mask = (userInput = '') => {
  const resMask = [/[1-9]/, '\''];
  if (userInput[2] === '1') {
    resMask.push(/[1]/, /[0-1]/, '"');
  } else {
    resMask.push(/[0-9]/, '"');
  }
  return resMask;
};

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const validationSchema = Yup.object().shape({
  // email: Yup.string()
  //   .email('Email must be a valid email')
  //   .required('Email is required'),
  weight: Yup.number()
    .required('Must be a number'),
  // bodyfat: Yup.number()
  //   .required('Must be a number'),
});

const passwordValidationSchema = Yup.object().shape({
  password: Yup.string()
    .min(6)
    .required('Password is required'),
  password_confirmation: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password'), null], 'Passwords do not match'),
});

const mapState = (state => ({
  fileUpload: getUiState('fileUpload')(state),
  settings: coreSelectors.getProfile('user_settings')(state),
}));

const EditProfile = ({
  clientInfo,
  profileId,
}: Props) => {
  const profile = clientInfo.user_profile;
  const { settings, fileUpload } = useSelector(mapState, shallowEqual);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMe = profileId === clientInfo.id;

  const onSubmit = useCallback((values: any, actions: any) => {
    const {
      email,
      height,
      units,
      ...rest
    } = values;

    dispatch(
      coreActions.requestUpdateClientInfo({
        id: profile.id,
        payload: {
          ...rest,
          email: email || null,
          height: (height && units === 'imperial')
            ? data.feetInchesToInches(height)
            : height || null,
        },
        callbacks: actions,
      }),
    );

    if (settings.units_system !== units) {
      dispatch(
        coreActions.requestUpdateSettings({
          body: {
            units_system: units,
          },
          settingsId: clientInfo.user_settings.id,
        }),
      );
    }
  }, [dispatch, profile.id]);

  const onSubmitPassword = useCallback((body: any, actions: any) => {
    dispatch(
      coreActions.requestUpdateUser({
        body,
        callbacks: {
          ...actions,
          onSuccess: () => {
            actions.resetForm({ password: '', password_confirmation: '' });
            toast.success('Your password was updated');
          },
        },
      }),
    );
  }, [dispatch])

  return (
    <Flex width="100%" align="stretch">
      <S.LeftBar>
        <Flex
          height="10%"
          width="100%"
          align='center'
        >
          <S.Back
            icon={<SVG.ChevronLeft size={18} />}
            appearance="link"
            onClick={() => {
              navigate(-1);
            }}
          >
            Back
          </S.Back>
        </Flex>
        <Flex
          column
          spacing={40}
          height="70%"
          width="100%"
          align="center"
        >
          <Text
            size="24px"
            color="#fff"
            weight={800}
          >
            {[profile.first_name, profile.last_name].filter(Boolean).join(' ') || '—'}
          </Text>
          <UploadPhoto
            src={profile?.avatar?.large?.url}
            width={185}
            height={185}
            uploadProgress={fileUpload[profile.id]?.progress || 0}
            onChange={(file) => {
              dispatch(
                coreActions.requestUploadClientAvatar({
                  file,
                  profileId: profile.id,
                }),
              );
            }}
          />
          {!isMe && (
            <div className="client-notes-wrapper">
              <ClientNotes clientId={clientInfo.id} />
            </div>
          )}
        </Flex>
        <Flex
          height="20%"
          width="100%"
          align="flex-end"
        >
          {isMe && (
            <S.ResetTutorial
              onClick={() => {
                // const tourSteps = ['thanks_bar', 'navigation_bar',
                // 'profile_avatar', 'help_icon', 'next_steps'];
                dispatch(
                  coreActions.requestUpdateSettings({
                    body: {
                      is_tutorial_finished: false,
                      web_onboarding_video_seen: false,
                      tutorial_steps: [],
                    },
                    settingsId: clientInfo.user_settings.id,
                  }),
                );
              }}
            >
              Reset Tutorial
            </S.ResetTutorial>
          )}
        </Flex>
      </S.LeftBar>
      <Flex
        column
        width="60%"
        height="100%"
        padding="30px"
        scrollY
      >
        <Formik
          initialValues={{
            birthday: profile.birthday,
            bodyfat: profile.bodyfat || '',
            email: clientInfo.email || '',
            first_name: profile.first_name || '',
            gender: profile.gender || 'male',
            last_name: profile.last_name || '',
            location: profile.location || '',
            employer: profile.employer || '',
            height: settings.units_system === 'imperial'
              ? data.inchesToFeetInches(+profile.height)
              : Math.round(profile.height),
            timezone: profile.timezone,
            weight: Math.round(profile.weight),
            zip: profile.zip || '',
            units: settings.units_system || 'imperial',
          }}
          // enableReinitialize
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {(formikProps) => (
            <Flex
              column
              width="100%"
              as="form"
              spacing={15}
              onSubmit={formikProps.handleSubmit}
            >
              <Text
                weight={500}
                size="13px"
              >
                Sex
              </Text>
              <Field
                name="male"
                label="Male"
                id="male"
                checked={formikProps.values.gender === 'male'}
                onChange={() => {
                  formikProps.setFieldValue('gender', 'male');
                }}
                component={RadioField}
              />
              <Field
                name="female"
                label="Female"
                id="female"
                checked={formikProps.values.gender === 'female'}
                onChange={() => {
                  formikProps.setFieldValue('gender', 'female');
                }}
                component={RadioField}
              />
              <Field
                name="units"
                label="Units"
                options={data.unitsOptions}
                component={SelectField}
                isSearchable={false}
                maxMenuHeight={500}
                onChange={(option: any, oldValue: any) => {
                  if (option?.value !== oldValue?.value) {
                    if (formikProps.values.weight) {
                      const weight = Math.round(
                        data.weightConvert(
                          formikProps.values.weight,
                          oldValue.value,
                          option.value,
                        ),
                      );
                      formikProps.setFieldValue('weight', weight || '');
                    }

                    if (formikProps.values.height) {
                      let rawValue = formikProps.values.height;
                      if (oldValue.value === 'imperial') {
                        rawValue = Math.floor(data.feetInchesToInches(rawValue));
                      }

                      let height = data.heightConvert(
                        rawValue,
                        oldValue.value,
                        option.value,
                      );

                      if (option.value === 'imperial') {
                        height = data.inchesToFeetInches(height);
                      }

                      formikProps.setFieldValue('height', height || '');
                    }
                  }
                }}
                theme={{
                  borderRadius: 5,
                }}
                styles={{
                  option: provided => ({
                    ...provided,
                    fontSize: '14px',
                    lineHeight: '12px',
                    fontWeight: 500,
                  }),
                  valueContainer: provided => ({
                    ...provided,
                    fontSize: '15px',
                    lineHeight: '15px',
                    fontWeight: 500,
                  }),
                }}
              />
              <Field
                name="weight"
                label={`Weight (${formikProps.values.units === 'imperial' ? 'lbs' : 'kg'})`}
                type="number"
                component={TextField}
                autoComplete="off"
              />
              <Field
                mask={formikProps.values.units === 'imperial' ? mask : null}
                name="height"
                label={`Height (${formikProps.values.units === 'imperial' ? 'ft\' in' : 'cm'})`}
                component={TextField}
                autoComplete="off"
              />
              <Field
                name="bodyfat"
                label="Bodyfat"
                type="number"
                component={TextField}
                autoComplete="off"
              />
              <Field
                name="first_name"
                label="First name"
                component={TextField}
                autoComplete="off"
              />
              <Field
                name="last_name"
                label="Last name"
                component={TextField}
                autoComplete="off"
              />
              <Field
                name="email"
                label="Email"
                component={TextField}
                autoComplete="off"
              />
              <S.TimezonePicker>
                <Field
                  name="timezone"
                  label="Time Zone"
                  options={timezonesOptions}
                  component={SelectField}
                  isSearchable={false}
                  maxMenuHeight={500}
                  placeholder="-- Select Time Zone --"
                  theme={{
                    borderRadius: 5,
                  }}
                  styles={{
                    option: provided => ({
                      ...provided,
                      fontSize: '12px',
                      lineHeight: '12px',
                      fontWeight: 500,
                    }),
                    valueContainer: provided => ({
                      ...provided,
                      fontSize: '12px',
                      lineHeight: '12px',
                      fontWeight: 500,
                    }),
                    control: provided => ({
                      ...provided,
                      // height: '30px',
                      minHeight: '30px',
                    }),
                  }}
                />
              </S.TimezonePicker>
              <Flex
                width="100%"
                justify="space-between"
              >
                <Flex
                  width="65%"
                >
                  <Field
                    name="location"
                    label="Location"
                    component={TextField}
                    autoComplete="off"
                  />
                </Flex>
                <Flex
                  width="30%"
                >
                  <Field
                    name="zip"
                    label="ZIP"
                    component={TextField}
                    autoComplete="off"
                    maxLength={10}
                  />
                </Flex>
              </Flex>
              <Field
                name="employer"
                label="Occupation"
                component={TextField}
                autoComplete="off"
              />
              <Field
                name="birthday"
                label="Date of Birth"
                placeholderText="Your Birthday"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                component={DatePickerField}
                onChange={(value: Date) => {
                  if (value) {
                    formikProps.setFieldValue('birthday', formatDate(value))
                  }
                }}
              />
              <Flex
                width="100%"
                justify="flex-end"
                spacing={20}
              >
                <Button
                  ghost
                  appearance="primary"
                  type="submit"
                  spacing="large"
                  isLoading={formikProps.isSubmitting}
                  isDisabled={formikProps.isSubmitting}
                >
                  Save Profile
                </Button>
              </Flex>
            </Flex>
          )}
        </Formik>
        {isMe && (
          <Formik
            initialValues={{
              password: '',
              password_confirmation: '',
            }}
            validationSchema={passwordValidationSchema}
            onSubmit={onSubmitPassword}
          >
            {(formikProps) => (
              <Flex
                column
                width="100%"
                as="form"
                spacing={15}
                onSubmit={formikProps.handleSubmit}
              >
                <Text
                  weight={500}
                  size="13px"
                >
                  Change password
                </Text>
                <Field
                  name="password"
                  component={PasswordField}
                  placeholder="Enter new Password"
                  autoComplete="off"
                />
                <Field
                  name="password_confirmation"
                  component={PasswordField}
                  placeholder="Confirm new Password"
                  autoComplete="off"
                />
                <Flex
                  width="100%"
                  justify="flex-end"
                  spacing={20}
                >
                  <Button
                    ghost
                    appearance="primary"
                    type="submit"
                    spacing="large"
                    isLoading={formikProps.isSubmitting}
                    isDisabled={formikProps.isSubmitting}
                  >
                    Save Password
                  </Button>
                </Flex>
              </Flex>
            )}
          </Formik>
        )}
      </Flex>
    </Flex>
  );
};

export default EditProfile;
